import React from 'react';
import { FormattedMessage } from 'react-intl';

import { useSelector } from 'hooks';
import { computeTotalPrice } from 'utils/priceUtils';

import { Advice } from 'types/Checklist';
import AdviceStatus from 'types/AdviceStatus';
import type { Extra } from 'types/ExtrasForm';
import type { RepairOrder, SelfService } from 'modules/selfServices/types/SelfService';

import { getSelectedSelfService, getValueAddedTax } from 'modules/selfServices/selectors';

import Item from './Item';
import TotalLine from './TotalLine';

export interface ListProps {
  title: React.ReactNode;
  items: Extra[] | RepairOrder[];
  onRemove?: (adviceId: string) => () => void,
  showHiddenPriceWarning?: boolean,
  hideTotals?: boolean,
  getAdvices?: (selfService: SelfService) => Array<Advice>,
}

const List: React.FC<ListProps> = ({ items, title, onRemove, showHiddenPriceWarning, hideTotals, getAdvices }) => {
  const vat = useSelector(getValueAddedTax);
  const selfService = useSelector(getSelectedSelfService);

  const grouped = React.useMemo(() => items.reduce<Record<string, ListProps['items']>>(
    (acc, item) => {
      let workOrderTag = '';
      if ('workOrderTag' in item) {
        workOrderTag = item.workOrderTag ?? '';
      }

      return {
        ...acc,
        [workOrderTag]: [...(acc[workOrderTag] ?? []), item] as ListProps['items'],
      };
    },
    {},
  ), [items]);

  const groupedTotals = React.useMemo(() => Object.entries(grouped).map(([workOrderTag, data]) => ({
    label: workOrderTag || title,
    value: computeTotalPrice(data, vat),
  })), [grouped, vat, title]);

  const isRemovable = React.useCallback((adviceId: string) => {
    if (getAdvices) {
      const advices = getAdvices(selfService);
      // Only answered advice during the flow can be removed.
      return advices.some(({ adviceId: id, status }) => adviceId === id && status !== AdviceStatus.ACCEPTED);
    }
    return false;
  }, [getAdvices, selfService]);

  return (
    <>
      <div className="border-b border-secondary pb-2xs mt-l mb-2xs">
        <h2 className="text-s leading-xl border-b border-secondary">
          {title}
          <span className="ml-1 text-xs font-normal text-low">
            <FormattedMessage
              id="basket.nbItems"
              defaultMessage="({nbItems, plural, one {# item} other {# items}})"
              values={{ nbItems: items.length }}
            />
          </span>
        </h2>
        {Object.entries(grouped).map(([workOrderTag, data]) => (
          <ul key={workOrderTag} className="flex flex-col gap-4 mt-6 first-of-type:mt-2">
            {workOrderTag && (
              <li className="bg-input-bg font-bold kiosk:text-3xl p-2 rounded-card -mb-2">
                {workOrderTag}
              </li>
            )}
            {data.map((item, index) => {
              const key = 'adviceId' in item ? item.adviceId : index;
              const itemProps = 'adviceId' in item ? { onRemove: isRemovable(item.adviceId) ? onRemove?.(item.adviceId) : undefined } : {};

              return (
                <li key={key}>
                  <Item
                    {...item}
                    {...itemProps}
                    vat={vat}
                  />
                </li>
              );
            })}
          </ul>
        ))}
      </div>

      {!hideTotals && groupedTotals.map(({ value, label }, index) => (
        <TotalLine key={index} price={value} title={label} showHiddenPriceWarning={showHiddenPriceWarning} />
      ))}
    </>
  );
};

export default List;
